/* Hallmark · component: page-map capsule · genre: playful · theme: project tokens (orange --primary)
 * anchor: fixed, docks 8px under the header's floating pill (top 5.25rem · h-11 mobile / h-12 desktop)
 * states: default · hover · focus-visible · active · selected
 *   (disabled/loading/error/success: N/A — every control is an in-page anchor with no async work)
 * contrast (measured, not estimated — against the white/85 glass over #F4F5F9):
 *   active lozenge  --primary-ink on primary/10  4.69:1  AA pass
 *   active chip     white on --primary-ink       5.27:1  AA pass
 *   inactive label  gray-600 on glass            4.77:1  AA pass
 *   price label     gray-900 on glass           17.52:1  AAA pass
 *   CTA             white on --primary           3.09:1  AA FAIL — inherited from the
 *     site-wide `bg-primary text-white` button (header "Đặt vé", BookingPriceRail,
 *     MobileCartBottom all share it). Left as-is rather than making one button diverge;
 *     the fix is to move --primary to --primary-ink across all of them at once.
 * pre-emit critique: P4 H5 E4 S5 R5 V4
 */
import { useFloatingNav } from '@/components/web/layouts/header/use-floating-nav';
import { cn } from '@/lib/utils';
import { Fragment, useCallback, useEffect, useRef } from 'react';

const formatVND = (amount: number) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);

/** Chips dissolve into the capsule's curve instead of being guillotined by a rectangle. */
const EDGE_FADE = 'linear-gradient(to right, transparent 0, #000 12px, #000 calc(100% - 12px), transparent 100%)';

const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export interface PageMapSection {
  id: string;
  label: string;
}

export interface GroupMapItem {
  id: string;
  label: string;
}

interface PageMapBarProps {
  sections: PageMapSection[];
  activeId: string | null;
  groups?: GroupMapItem[];
  activeGroupId?: string | null;
  packagesSectionId?: string;
  minPrice?: number | null;
}

/**
 * Wayfinder for the search page — the second, subordinate member of the header's pill family.
 *
 * It borrows the header's surface wholesale (rounded-full, `bg-white/85`, the same blur and
 * the same `--ease-morph` curve) so the two read as one system, then steps *down* on the two
 * axes that mark hierarchy: a shorter-throw shadow with no brand-red bloom, and a narrower,
 * content-hugging width. A full-bleed bar under a floating pill is what read as broken.
 *
 * Three rules hold it together:
 *
 * 1. It is `fixed`, not `sticky` — like the header pill it owns no layout space, so there is
 *    no collapsing height to animate and nothing below it ever shifts.
 * 2. It owns its own visibility. `isFloating` is the half that matters: before the 80px
 *    threshold the header is still a solid bar occupying 0–80px, and the capsule would land
 *    on top of it.
 * 3. `z-40` puts it under the header (`z-50`) and therefore under the mega-panel's scrim, so
 *    opening the menu dims the capsule instead of letting it punch through.
 */
export function PageMapBar({ sections, activeId, groups = [], activeGroupId = null, packagesSectionId = 'packages', minPrice }: PageMapBarProps) {
  const isFloating = useFloatingNav();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const activeChipRef = useRef<HTMLButtonElement>(null);

  const shown = isFloating && activeId !== null;
  const showGroups = activeId === packagesSectionId && groups.length > 1;
  const hasPrice = minPrice != null && minPrice > 0;

  // Centre the active chip by driving the scroller directly. `scrollIntoView` would also move
  // the *document*, nudging the very scroll position the scroll-spy just read — the chip and
  // the page would fight each other for a frame on every group change.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const chip = activeChipRef.current;
    if (!scroller || !chip || !showGroups) return;

    const left = chip.offsetLeft - (scroller.clientWidth - chip.offsetWidth) / 2;
    scroller.scrollTo({ left: Math.max(0, left), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }, [activeGroupId, showGroups]);

  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  }, []);

  return (
    <nav aria-label="Mục lục trang" className="pointer-events-none fixed inset-x-0 top-[5.25rem] z-40 flex justify-center px-3">
      <div
        className={cn(
          'pointer-events-auto flex h-11 w-fit max-w-[min(56rem,calc(100vw_-_1.5rem))] items-center gap-0.5 rounded-full px-1.5 lg:h-12 lg:gap-1 lg:px-2',
          'bg-white/85 shadow-(--shadow-dock) ring-1 ring-black/5 backdrop-blur-[18px] backdrop-saturate-150',
          // One curve, shared with the header — two timings would read as two animations.
          'transition-[opacity,transform,visibility] duration-[360ms] ease-(--ease-morph)',
          shown ? 'visible translate-y-0 scale-100 opacity-100' : 'invisible -translate-y-2 scale-[0.98] opacity-0',
          'motion-reduce:translate-y-0 motion-reduce:scale-100 motion-reduce:transition-[opacity,visibility] motion-reduce:duration-150',
        )}
      >
        {sections.map((section) => (
          <Fragment key={section.id}>
            <button
              type="button"
              onClick={() => scrollTo(section.id)}
              aria-current={activeId === section.id ? 'true' : undefined}
              className={cn(
                'flex-shrink-0 cursor-pointer rounded-full px-3 py-1.5 text-sm whitespace-nowrap transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-(--primary-ink)/70 focus-visible:outline-none lg:px-3.5',
                activeId === section.id
                  ? 'bg-primary/10 font-semibold text-(--primary-ink)'
                  : 'text-gray-600 hover:bg-gray-900/[0.04] hover:text-gray-900',
              )}
            >
              {section.label}
            </button>

            {/* The chip cluster grows out of the capsule right where the packages lozenge ends,
                so the capsule widens from the inside rather than jumping to a new width. */}
            {section.id === packagesSectionId && groups.length > 1 && (
              <div
                className={cn(
                  'flex min-w-0 items-center overflow-hidden transition-[max-width,opacity] duration-[360ms] ease-(--ease-morph) motion-reduce:transition-none',
                  showGroups ? 'max-w-[13rem] opacity-100 sm:max-w-xs lg:max-w-sm' : 'max-w-0 opacity-0',
                )}
              >
                <span className="mx-1.5 h-5 w-px flex-shrink-0 bg-(--pill-border) lg:mx-2" aria-hidden="true" />
                <div
                  ref={scrollerRef}
                  className="scrollbar-hide flex min-w-0 gap-1.5 overflow-x-auto px-1 py-1.5"
                  style={{ maskImage: EDGE_FADE, WebkitMaskImage: EDGE_FADE }}
                >
                  {groups.map(({ id, label }) => (
                    <button
                      key={id}
                      type="button"
                      ref={activeGroupId === id ? activeChipRef : undefined}
                      onClick={() => scrollTo(id)}
                      aria-current={activeGroupId === id ? 'true' : undefined}
                      className={cn(
                        'flex-shrink-0 cursor-pointer rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-(--primary-ink)/70 focus-visible:outline-none',
                        activeGroupId === id ? 'bg-(--primary-ink)/90 text-white' : 'bg-(--pill-bg) text-gray-600 hover:text-white hover:bg-(--primary-ink)',
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Fragment>
        ))}

        {/* Desktop only — BookingPriceRail retires the moment #packages scrolls into view, and
            MobileCartBottom owns the mobile CTA, so this is the one persistent anchor here. */}
        {hasPrice && (
          <div className="hidden flex-shrink-0 items-center gap-2.5 lg:flex">
            <span className="ml-1 h-5 w-px bg-(--pill-border)" aria-hidden="true" />
            <p className="text-sm whitespace-nowrap text-gray-600">
              Từ <span className="font-semibold text-gray-900">{formatVND(minPrice)}</span>
            </p>
            <button
              type="button"
              onClick={() => scrollTo(packagesSectionId)}
              className="bg-primary flex-shrink-0 cursor-pointer rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap text-white transition-[background-color,transform] duration-200 hover:bg-(--brand-orange-hover) focus-visible:ring-2 focus-visible:ring-(--primary-ink)/70 focus-visible:ring-offset-2 focus-visible:outline-none active:scale-[0.97] motion-reduce:transition-[background-color]"
            >
              Chọn dịch vụ
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}
